import React from 'react';
import { Wallet, Clock, X, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';

export interface WalletDetailsData {
  name: string;
  currency: string;
  deposit: number;
  withdrawal: number;
  reservedDeposit: number;
  reservedWithdrawal: number;
  available: number;
  inFlight: number;
  total: number;
  gradient?: string;
  iconUrl?: string;
  iconColor?: string;
}

interface WalletDetailsModalProps {
  data: WalletDetailsData | null;
  onClose: () => void;
}

const DEFAULT_GRADIENT = 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)';

const WalletDetailsModal: React.FC<WalletDetailsModalProps> = ({ data, onClose }) => {
  const { t, formatCurrency } = useLocalization();

  if (!data) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-5 text-white" style={{ background: data.gradient || DEFAULT_GRADIENT }}>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/30">
              {data.iconUrl ? (
                <img
                  src={data.iconUrl}
                  alt={data.name}
                  className="w-7 h-7 object-contain"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <Wallet className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold">{data.name}</h3>
              <p className="text-xs text-white/80">
                {t('balances.detailsTitle') || 'Détail du portefeuille'} — {data.currency}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Available */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-emerald-700" />
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                {t('balances.available') || 'Disponible'}
              </span>
            </div>
            <p className="text-2xl font-bold text-emerald-900">
              {formatCurrency(data.available, data.currency)}
            </p>
            <p className="text-xs text-emerald-700/70 mt-1">
              {t('balances.availableHint') || 'Utilisable maintenant'}
            </p>
          </div>

          {/* Reserved (if any) */}
          {data.inFlight > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-700" />
                <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                  {t('balances.inFlight') || 'Réservé'}
                </span>
              </div>
              <p className="text-xl font-bold text-amber-900">
                {formatCurrency(data.inFlight, data.currency)}
              </p>
              <p className="text-xs text-amber-700/70 mt-1">
                {t('balances.inFlightHint') || 'Bloqué pour des paiements ou transferts en cours'}
              </p>
            </div>
          )}

          {/* Decomposition */}
          <div className="rounded-xl border border-gray-200 p-4">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-3">
              {t('balances.decomposition') || 'Décomposition'}
            </p>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ArrowDownCircle className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm text-gray-700">{t('balances.payIn') || 'Pay-in'}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(data.deposit, data.currency)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ArrowUpCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-700">{t('balances.payOut') || 'Pay-out'}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(data.withdrawal, data.currency)}
                </span>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 mt-2 italic">
              {t('balances.decompositionHint') || 'Pay-in + Pay-out = Total. Le Réservé est déjà inclus dans ces deux poches.'}
            </p>
          </div>

          {/* Total */}
          <div className="flex items-center justify-between pt-1">
            <span className="text-sm font-semibold text-gray-600">
              {t('balances.total') || 'Total'}
            </span>
            <span className="text-lg font-bold text-gray-900">
              {formatCurrency(data.total, data.currency)}
            </span>
          </div>

          {/* Help text */}
          <p className="text-[11px] text-gray-500 leading-relaxed bg-gray-50 border border-gray-100 rounded-lg p-3">
            {t('balances.detailsHelp') ||
              "« Disponible » : ce que vous pouvez utiliser tout de suite. « Réservé » : montants bloqués pour des paiements ou transferts en cours."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WalletDetailsModal;
